import {faCheck} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {groupBy} from 'lodash';
import React, {useMemo, useState} from 'react';
import {Button, Dropdown, Form, Icon} from 'semantic-ui-react';
import {DataType} from '../const/crud';
import {Company} from '../models/Company';
import {JobStage} from '../models/Job';
import {User} from '../models/User';
import {DataHelper, ValueTemplate} from '../utils/dataHelper';
import {DataActionType, DataTableType, SortDirection} from './Data/DataConst';
import DataTable from './Data/DataTable';

interface ChecklistProps {
  stage: JobStage;
  stageData: any;
  handleDataAction: any;
  users: User[];
  loggedInCompany: Company;
  loggedInUser: User;
  state: any;
}

const Checklist: React.FunctionComponent<ChecklistProps> = (
  props: ChecklistProps,
) => {
  const [importGroup, setImportGroup] = useState<any>();

  const importOptions = props.loggedInCompany?.checklists?.map((i) => {
    return {
      key: i._id,
      text: i.name,
      value: i._id,
      description: i.checklistType,
    };
  });

  const checklist = props.stageData[props.stage].checklist || [];

  const grouped = useMemo(() => {
    const groups: Record<string, any[]> = groupBy(checklist, (item: any) => item.group || '');
    const entries = Object.entries(groups);
    // Named groups first (sorted alphabetically), ungrouped last
    const named = entries.filter(([key]) => key !== '').sort(([a], [b]) => a.localeCompare(b));
    const ungrouped = entries.find(([key]) => key === '');
    return ungrouped ? [...named, ungrouped] : named;
  }, [checklist]);

  const cols = [
    {
      title: 'Task',
      dataField: 'text',
      type: DataTableType.PLAIN,
      mobileDisplay: true,
      width: 6,
    },
    {
      title: 'Status',
      dataField: 'status',
      type: DataTableType.STATUS_CHECK,
      mobileDisplay: true,
      width: 3,
    },
    {
      title: 'Note',
      dataField: 'note',
      type: DataTableType.PLAIN,
      mobileDisplay: false,
      width: 4,
    },
    {
      title: 'Assigned To',
      dataField: 'assignedTo',
      dataProcess: {
        action: (value: string) => {
          switch (value) {
            case 'onsite': return 'Onsite Users';
            case 'admin': return 'Admin Users';
            case 'all': return 'All';
            default: return value || '';
          }
        },
      },
      type: DataTableType.STRING,
      mobileDisplay: false,
      width: 2,
    },
    {
      title: 'Completed By',
      dataField: 'completedBy',
      dataProcess: {
        action: DataHelper.getValueFromData,
        data: props.users,
        field: ValueTemplate.NAME,
      },
      type: DataTableType.STRING,
      mobileDisplay: false,
      width: 3,
    },
    {
      title: 'Date Completed',
      dataField: 'completed',
      type: DataTableType.DATE_TIME,
      mobileDisplay: true,
      width: 2,
    },
  ];

  const actions = [
    {
      title: 'Edit',
      icon: 'pencil' as const,
      className: 'spaceBelow',
      action: DataActionType.EDIT_CHECKLIST,
      data: {stage: props.stage},
    },
    {
      title: 'Delete',
      icon: 'delete' as const,
      className: 'redButton',
      action: DataActionType.DELETE_CHECKLIST,
      data: {stage: props.stage},
    },
  ];

  return (
    <div className={'Checklist'}>
      <div className={'AddTask'}>
        <Form.Field>
          <h3>Import Checklist</h3>
          <p>
            Select a checklist to import from your pre-defined templates. These
            templates can be edited in your 'Companies' page.
          </p>
          <Dropdown
            placeholder={`Select Checklist`}
            fluid
            selection
            options={importOptions}
            value={importGroup}
            onChange={(e, data) => setImportGroup(data.value)}
          />
        </Form.Field>
        <Button
          disabled={!importGroup}
          primary
          onClick={() => {
            const tasks = props.loggedInCompany.checklists.find(
              (i) => i._id === importGroup,
            )?.tasks;

            props.handleDataAction(
              DataActionType.IMPORT_CHECKLIST_TEMPLATE,
              importGroup,
              {
                stage: props.stage,
                selected: props.state,
                tasks,
              },
            );
          }}
        >
          Import Tasks
        </Button>
      </div>

      {grouped.length === 0 ? (
        <DataTable
          title="Checklist"
          description={'Add a checklist of items that are required for this job.'}
          titleIcon={<FontAwesomeIcon icon={faCheck} />}
          data={[]}
          dataType={DataType.CHECKLIST}
          primaryAction={DataActionType.CREATE_CHECKLIST}
          handleAction={props.handleDataAction}
          addParams={{stage: props.stage}}
          pageSize={5}
          sortField={['created']}
          sortDirection={[SortDirection.ASC]}
          actions={actions}
          cols={cols}
        />
      ) : (
        <>
          <div className="DataTableContainer">
            <div className="DataTableHeader">
              <div className="DataTableTitleContainer">
                <FontAwesomeIcon icon={faCheck} />
                <h1>Checklist</h1>
              </div>
              <Button
                icon
                primary
                labelPosition="left"
                onClick={() =>
                  props.handleDataAction(DataActionType.CREATE_CHECKLIST, null, {stage: props.stage, selected: props.state})
                }
              >
                <Icon name="add" />
                Add Task
              </Button>
            </div>
            <p>Add a checklist of items that are required for this job.</p>
          </div>
          {grouped.map(([groupName, items], index) => (
            <div key={groupName || '__ungrouped'}>
              {groupName ? (
                <h3 style={{marginTop: index > 0 ? 20 : 0, marginBottom: 5}}>
                  {groupName}
                </h3>
              ) : grouped.length > 1 ? (
                <h3 style={{marginTop: index > 0 ? 20 : 0, marginBottom: 5, color: '#999'}}>
                  Ungrouped
                </h3>
              ) : null}
              <DataTable
                title=""
                data={items}
                dataType={DataType.CHECKLIST}
                handleAction={props.handleDataAction}
                addParams={{stage: props.stage, group: groupName || undefined, selected: props.state}}
                pageSize={50}
                sortField={['created']}
                sortDirection={[SortDirection.ASC]}
                actions={actions}
                cols={cols}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Checklist;
